import React, { useState } from "react";
import { colors2, UI_BODY_SM } from "oolib";
import { IntegrationModalShell } from "./IntegrationModalShell";
import { ConnectionDetailsCard } from "./ConnectionDetailsCard";
import { DatasetsCard } from "./DatasetsCard";
import { SyncCard } from "./SyncCard";
import { AuthExpiredCard } from "./AuthExpiredCard";
import { useManagedIntegration } from "./hooks";

/**
 * An integration's manage popup: its connection and the datasets it imports
 * into. "Configure" on a dataset swaps to its sync settings, with a back arrow,
 * and the integration's own EditImport ({ conn, dataset, onSave }) below them.
 */
export function ManageIntegrationModal({
  provider,
  EditImport,
  name,
  Icon,
  onClose,
  onDisconnect,
  onAddDataset,
  onImportMore,
}) {
  const m = useManagedIntegration(provider, { name, onClose });
  // Id of the dataset whose configuration is open; looked up each render so the
  // view follows fresh data, and drops back to the list if it goes away.
  const [configuringId, setConfiguringId] = useState(null);
  // A stale credential can't sync anything, so it replaces both views.
  const configuring =
    !m.isStale && m.datasets.find((d) => d.id === configuringId);

  if (configuring) {
    return (
      <IntegrationModalShell
        title={configuring.title}
        onBack={() => setConfiguringId(null)}
        onClose={onClose}
      >
        <SyncCard
          dataset={configuring}
          canSync={m.canSync}
          canPublish={m.canPublish}
          justSaved={m.savedScheduleFor === configuring.id}
          onSyncNow={m.sync}
          onApplyFrequency={m.applyFrequency}
        />
        {m.canPublish && (
          <EditImport
            conn={m.conn}
            dataset={configuring}
            onSave={(adapterOptions) =>
              onImportMore(configuring.id, adapterOptions)
            }
          />
        )}
      </IntegrationModalShell>
    );
  }

  return (
    <IntegrationModalShell title={name} Icon={Icon} onClose={onClose}>
      {m.isLoading && (
        <UI_BODY_SM style={{ color: colors2.grey60 }}>Loading…</UI_BODY_SM>
      )}
      {m.errorMsg && (
        <UI_BODY_SM style={{ color: colors2.red }}>{m.errorMsg}</UI_BODY_SM>
      )}
      {!m.isLoading && m.isStale && (
        <AuthExpiredCard
          name={name}
          canPublish={m.canPublish}
          connect={m.connect}
          isConnecting={m.isConnecting}
        />
      )}
      {!m.isLoading && m.conn && !m.isStale && (
        <>
          <ConnectionDetailsCard
            conn={m.conn}
            canDelete={m.canDelete}
            onDisconnect={onDisconnect}
          />
          <DatasetsCard
            name={name}
            datasets={m.datasets}
            canSync={m.canSync}
            canPublish={m.canPublish}
            onAddDataset={onAddDataset}
            onConfigure={(d) => setConfiguringId(d.id)}
            onSync={m.sync}
          />
        </>
      )}
    </IntegrationModalShell>
  );
}

export default ManageIntegrationModal;
